import { createI18n, type I18n, type NamedValue } from 'vue-i18n'
import { setActiveLocale } from '../utils/activeLocale'

export interface SupportedLocale {
  code: string
  name: string
  flag?: string
}

export const SUPPORTED_LOCALES: SupportedLocale[] = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
]

export const DEFAULT_LOCALE = 'fr'

export const SUPPORTED_LOCALE_CODES = SUPPORTED_LOCALES.map((locale) => locale.code)

export function isSupportedLocale(code: string): boolean {
  return SUPPORTED_LOCALE_CODES.includes(code)
}

export type LocaleMessages = {
  [key: string]: LocaleMessages | string
}

export type Messages = {
  [locale: string]: LocaleMessages
}

// --- Locale discovery ---

export interface LocaleModules {
  [path: string]: { default: Record<string, unknown> }
}

/**
 * The app and the section a locale file stands for: `…/apps/<app>/locales/<locale>.js` is the
 * whole of `<app>`, its first-level keys being the sections, and
 * `…/apps/<app>/locales/<locale>.<section>.js` is `<app>.<section>`.
 */
function parseLocalePath(path: string): { appName: string; section: string | null } | null {
  const match = path.match(/apps\/([^/]+)\/locales\/(?:en|fr)(?:\.([^.]+))?\.js$/)
  if (!match?.[1]) return null
  return { appName: match[1], section: match[2] ?? null }
}

/**
 * One locale's messages, out of an eager `import.meta.glob` of the apps' `locales/<locale>*.js`
 * files: one namespace per app, named after its directory.
 */
export function buildLocaleMessages(modules: LocaleModules): LocaleMessages {
  const result: Record<string, LocaleMessages> = {}

  for (const [path, module] of Object.entries(modules)) {
    const parsed = parseLocalePath(path)
    if (!parsed) continue

    const { appName, section } = parsed
    const app = (result[appName] ??= {})

    if (section) {
      app[section] = module.default as LocaleMessages
    } else {
      for (const [sectionKey, sectionValue] of Object.entries(module.default)) {
        app[sectionKey] = sectionValue as LocaleMessages
      }
    }
  }

  return result
}

function detectBrowserLanguage(fallbackLocale: string = DEFAULT_LOCALE): string {
  const userSetLocale = localStorage.getItem('userSetLocale')
  if (userSetLocale) {
    return userSetLocale
  }

  const browserLang = navigator.language || navigator.languages?.[0] || fallbackLocale
  const langCode = browserLang.split('-')[0].toLowerCase()

  return SUPPORTED_LOCALE_CODES.includes(langCode) ? langCode : fallbackLocale
}

export interface I18nPluginOptions {
  messages: Messages
  defaultLocale?: string
  detectLanguage?: boolean
}

/** What {@link translate} reads: the global composer of the instance this page's entry created. */
let globalTranslate: ((key: string, named: NamedValue) => string) | null = null

export function createI18nPlugin(options: I18nPluginOptions): I18n {
  const { messages, defaultLocale = DEFAULT_LOCALE, detectLanguage = true } = options

  const locale = detectLanguage ? detectBrowserLanguage(defaultLocale) : defaultLocale

  // Published for the pure formatters, which cannot call `useI18n()`. Set here
  // as well as in the store so a page that never touches the language picker
  // still formats in the language it detected.
  setActiveLocale(locale)

  const i18n = createI18n({
    legacy: false,
    locale,
    fallbackLocale: defaultLocale,
    messages,
    globalInjection: true,
    missingWarn: false,
    fallbackWarn: false,
  })
  globalTranslate = (key, named) => i18n.global.t(key, named)
  return i18n
}

/**
 * A message in the displayed language, for code that runs outside a component's setup — a Pinia
 * store, which a timer, a route guard or another store may be the first to use — where `useI18n()`
 * throws (`MUST_BE_CALL_SETUP_TOP`). It goes through the instance's global composer, the one the
 * language picker switches, so it follows the locale. The key itself before any entry created one.
 */
export function translate(key: string, named: NamedValue = {}): string {
  return globalTranslate ? globalTranslate(key, named) : key
}
